import { Box, Typography, IconButton, InputBase } from '@mui/material';
import { useMemo, useState, useRef, type ReactNode } from 'react';
import { osirisTokens } from '@/theme/tokens';
import { Icon } from './Icon';

export type LogSeverity = 'info' | 'warn' | 'error';

export interface LogEntry {
  id: string;
  timestamp: string;
  severity: LogSeverity;
  /** Short tag shown inline before the message, e.g. INFO / WARN / ERROR. */
  tag?: string;
  message: ReactNode;
}

interface LogListProps {
  entries: LogEntry[];
  className?: string;
  /** When true, shows the ALL / WRN / ERR filter chips. */
  showFilter?: boolean;
  /** Infinite scroll: called when the list nears its bottom. */
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /** Extra toolbar icon(s), right of the search toggle (Figma toolbar 28693:28270). */
  action?: ReactNode;
}

type Filter = 'all' | 'warn' | 'error';

// Figma log List Item, state "wrn" (28376:16618): 5% tint + 2px States/system
// bar, text stays txt-primary. ERR mirrors it with States/system/error.
const SEVERITY_TINT: Record<LogSeverity, string> = {
  info: 'transparent',
  warn: 'rgba(237, 193, 42, 0.05)',
  error: 'rgba(248, 62, 77, 0.05)',
};

const SEVERITY_BAR: Record<LogSeverity, string> = {
  info: 'transparent',
  warn: '#edc12a', // States/system/warning
  error: '#f83e4d', // States/system/error
};

export function LogList({
  entries,
  className,
  showFilter = true,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  action,
}: LogListProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = rowsRef.current;
    if (!el || !onLoadMore || !hasMore || isLoadingMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) onLoadMore();
  };

  const handleSearchToggle = () => {
    if (searchOpen) {
      setSearchQuery('');
      setSearchOpen(false);
    } else {
      setSearchOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const visible = useMemo(() => {
    let result = entries;
    if (filter === 'warn') result = result.filter((e) => e.severity === 'warn');
    else if (filter === 'error') result = result.filter((e) => e.severity === 'error');
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          (typeof e.message === 'string' && e.message.toLowerCase().includes(q)) ||
          e.tag?.toLowerCase().includes(q) ||
          e.timestamp.includes(q),
      );
    }
    return result;
  }, [entries, filter, searchQuery]);

  return (
    <Box
      className={`log-list${className ? ' ' + className : ''}`}
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
    >
      {/* toolbar is hidden entirely when there are no logs (Figma 28693:28393) */}
      {showFilter && entries.length > 0 && (
        <Box
          className="log-list-filter"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            height: 36,
            borderBottom: `1px solid ${osirisTokens.border.primary}`,
            flexShrink: 0,
          }}
        >
          {searchOpen ? (
            <InputBase
              inputRef={inputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter logs…"
              className="log-list-search-input"
              sx={{
                flex: 1,
                fontFamily: osirisTokens.fonts.mono,
                fontSize: 12,
                color: osirisTokens.text.primary,
                '& input::placeholder': { color: osirisTokens.text.muted },
              }}
            />
          ) : (
            <>
              <FilterChip label="ALL" active={filter === 'all'} onClick={() => setFilter('all')} />
              <FilterChip
                label="WRN"
                active={filter === 'warn'}
                onClick={() => setFilter('warn')}
              />
              <FilterChip
                label="ERR"
                active={filter === 'error'}
                onClick={() => setFilter('error')}
              />
              <Box sx={{ flex: 1 }} />
            </>
          )}
          {/* Figma toolbar (28693:28270): search then arrow-expand, 16px apart */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              size="small"
              onClick={handleSearchToggle}
              className="log-list-search-btn"
              sx={{
                // project icon set at Icons/secondary (Figma To Do 28513:23740)
                color: searchOpen ? osirisTokens.text.primary : osirisTokens.icons.secondary,
              }}
            >
              <Icon name={searchOpen ? 'close-S' : 'search'} size={16} />
            </IconButton>
            {action}
          </Box>
        </Box>
      )}
      <Box
        ref={rowsRef}
        onScroll={handleScroll}
        className="log-list-rows"
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        {visible.map((entry) => (
          <LogRow key={entry.id} entry={entry} />
        ))}
        {visible.length === 0 && (
          // "No logs found" bar per the empty-drawer mock (Figma 28693:28393)
          <Box
            className="log-list-empty"
            sx={{
              m: 1,
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'center',
              borderRadius: '2px',
              bgcolor: 'rgba(110, 68, 245, 0.15)',
              fontFamily: osirisTokens.fonts.mono,
              fontSize: 11,
              letterSpacing: '0.02em',
              color: osirisTokens.text.secondary,
            }}
          >
            No logs found
          </Box>
        )}
        {isLoadingMore && (
          <Box
            sx={{
              p: 1,
              textAlign: 'center',
              fontFamily: osirisTokens.fonts.mono,
              fontSize: 11,
              color: osirisTokens.text.muted,
            }}
          >
            Loading more…
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      className={`log-filter-chip${active ? ' is-active' : ''}`}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 24,
        px: 1,
        borderRadius: '1px',
        fontFamily: osirisTokens.fonts.mono,
        fontSize: 12,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: active ? osirisTokens.text.secondary : osirisTokens.text.tertiary,
        bgcolor: active ? osirisTokens.bg.secondary : 'transparent',
        border: `1px solid ${active ? 'transparent' : osirisTokens.border.lighter}`,
        '&:hover': { color: osirisTokens.text.primary },
      }}
    >
      {label}
    </Box>
  );
}

interface LogRowProps {
  entry: LogEntry;
}

function LogRow({ entry }: LogRowProps) {
  return (
    <Box
      className={`log-row log-row-${entry.severity}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        minHeight: 24,
        pl: 1,
        pr: 0.5,
        py: 0.5,
        bgcolor: SEVERITY_TINT[entry.severity],
        borderLeft: `2px solid ${SEVERITY_BAR[entry.severity]}`,
        borderBottom: `1px solid ${osirisTokens.border.secondary}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography
        className="log-row-timestamp"
        sx={{
          flexShrink: 0,
          fontFamily: osirisTokens.fonts.body,
          fontSize: 13,
          letterSpacing: '0.02em',
          color: osirisTokens.text.secondary,
          whiteSpace: 'nowrap',
        }}
      >
        {entry.timestamp}
      </Typography>
      <Typography
        className="log-row-message"
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: osirisTokens.fonts.body,
          fontSize: 13,
          letterSpacing: '0.02em',
          color: osirisTokens.text.primary,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {entry.tag && `[${entry.tag}] `}
        {entry.message}
      </Typography>
    </Box>
  );
}
